import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import "./game.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { getDvSourceImageUrl, toCssUrlValue } from "../../functions/themeAssets";
import RulesModal from "./RulesModal";

const USER_DATA_KEY = "userData";
const DEFAULT_POINTS = 10;
const DEFAULT_TOTAL_QUESTIONS = 10;

// Same tokens as .mf-root's CSS fallback — kept in sync so the page-wide
// background (set on html/body, see the effect below) still shows this
// gradient when the theme has no uploaded background image.
const AMBIENT_GRADIENT_FALLBACK = [
  "radial-gradient(950px 660px at 10% -8%,  rgba(199,87,76,.26),  transparent 64%)",
  "radial-gradient(950px 660px at 92% 110%, rgba(46,139,103,.28), transparent 64%)",
  "radial-gradient(1150px 680px at 50% 34%, rgba(217,166,46,.20), transparent 66%)",
  "radial-gradient(700px 500px at 96% -4%,  rgba(46,139,103,.12), transparent 60%)",
  "radial-gradient(700px 500px at 4% 106%,  rgba(199,87,76,.12),  transparent 60%)",
].join(", ");

function readSessionUserData() {
  try {
    return JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function formatTime(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function parseTimeToSeconds(value) {
  if (value == null) return 0;
  const parts = String(value).trim().split(":").map((n) => Number(n));
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

function parseBool(value, fallback) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  const s = String(value).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
}

function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function launchConfetti(container, intensity) {
  const colors = ["#C7574C", "#2E8B67", "#D9A62E", "#E8B4AC", "#9ED4BC"];
  // Denser + staggered starting heights (not all from the same line just
  // above the top) so the burst reads as a full-screen shower from the
  // very first frame, instead of a thin band that takes a moment to
  // spread down and fill the screen.
  const count = intensity >= 0.8 ? 130 : 80;
  for (let i = 0; i < count; i++) {
    const el = document.createElement("span");
    el.className = "mf-confetti";
    el.style.left = `${Math.random() * 100}vw`;
    const startVh = -10 - Math.random() * 60;
    el.style.top = `${startVh}vh`;
    // Travel distance is computed per-piece from its own start height (not
    // a fixed 112vh) so every piece — no matter how far above the viewport
    // it started — still falls all the way past the bottom instead of
    // stopping short and freezing mid-screen once the animation ends.
    el.style.setProperty("--fall", `${115 - startVh}vh`);
    el.style.background = colors[i % colors.length];
    el.style.setProperty("--t", `${1.9 + Math.random() * 1.3}s`);
    el.style.setProperty("--d", `${Math.random() * 0.9}s`);
    el.style.setProperty("--r", `${Math.random() * 360}deg`);
    if (Math.random() > 0.5) el.style.borderRadius = "50%";
    container.appendChild(el);
    setTimeout(() => el.remove(), 4400);
  }
}

export default function GamePage() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);
  const storedUserData = useMemo(() => readSessionUserData(), []);
  const confettiHostRef = useRef(null);

  const userId = storedUserData?.userId || storedUserData?.userid || storedUserData?.id || "";
  const organizationId = storedUserData?.organizationId || "admin";
  const sessionId = storedUserData?.sessionId || "admin";
  const currentThemeName = themeData?.themename ?? themeData?.themeName ?? themeData?.themeId ?? "";

  // ── Dynamic config from the admin's theme data ──
  const pointsPerCorrect = Number(themeData?.points ?? DEFAULT_POINTS) || DEFAULT_POINTS;
  const totalWanted = Number(themeData?.total_question ?? DEFAULT_TOTAL_QUESTIONS) || DEFAULT_TOTAL_QUESTIONS;
  const shuffleEnabled = parseBool(themeData?.shuffle, true);
  // Admin-configured short blurbs shown under the Myth/Fact buttons (Rules
  // page) — optional, so only rendered when actually set.
  const optionOneDescription = String(themeData?.option_one_description ?? "").trim();
  const optionTwoDescription = String(themeData?.option_two_description ?? "").trim();

  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.matchMedia("(max-width: 640px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = () => setIsMobileViewport(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Dynamic background image: the admin's uploaded desktop/mobile theme background.
  const backgroundImageUrl = useMemo(() => {
    if (!themeData) return "";
    const key = isMobileViewport ? themeData.background_mob : themeData.background_desk;
    return key ? getDvSourceImageUrl(key) : "";
  }, [themeData, isMobileViewport]);

  const themeVars = useMemo(
    () => ({
      "--theme-accent": themeData?.button_color || undefined,
      "--theme-accent-tint": themeData?.icon_bg_color || undefined,
      "--theme-text": themeData?.textcolor || undefined,
      "--theme-button-text": themeData?.button_Textcolor || undefined,
      "--theme-card-bg": themeData?.text_bg_color || undefined,
      "--theme-option-one": themeData?.option_one_color || undefined,
      "--theme-option-two": themeData?.option_two_color || undefined,
    }),
    [themeData]
  );

  // Apply the theme background to the whole page (html + body), not just this
  // component's root, so it extends behind the app's outer header too instead
  // of stopping at .mf-root's own box. Falls back to the ambient gradient
  // when the theme has no uploaded background image.
  useEffect(() => {
    const targets = [document.documentElement, document.body];
    const image = backgroundImageUrl ? toCssUrlValue(backgroundImageUrl) : AMBIENT_GRADIENT_FALLBACK;
    for (const el of targets) {
      el.style.backgroundImage = image;
      el.style.backgroundSize = "cover";
      el.style.backgroundPosition = "top center";
      el.style.backgroundRepeat = "no-repeat";
      // Pinned to the viewport instead of the document: without this, `cover`
      // sizes against the full scrollable page height (which grows past the
      // viewport once content overflows), so the image stretches/scrolls out
      // of frame instead of staying fitted behind the content.
      el.style.backgroundAttachment = "fixed";
    }
    return () => {
      for (const el of targets) {
        el.style.removeProperty("background-image");
        el.style.removeProperty("background-size");
        el.style.removeProperty("background-position");
        el.style.removeProperty("background-repeat");
        el.style.removeProperty("background-attachment");
      }
    };
  }, [backgroundImageUrl]);

  // ── Game state ──
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [screen, setScreen] = useState("question"); // question | verdict — game completion navigates to /leaderboard directly, no in-page "results" screen
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [results, setResults] = useState([]);
  const [locked, setLocked] = useState(false);
  const [lastAnswer, setLastAnswer] = useState(null);
  const [shakeActive, setShakeActive] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const gameCompletedRef = useRef(false);
  const [reportId, setReportId] = useState(storedUserData?.reportId || storedUserData?.report_id || null);
  const autoAdvanceTimeoutRef = useRef(null);
  const shakeTimeoutRef = useRef(null);
  // Synchronous click-guard: state (`locked`) only flips *after* a render, so
  // two clicks fired faster than that render both still see the old value.
  // A ref updates immediately, so the second click is blocked for real.
  // Shared between pickAnswer and advanceToNext since they're two halves of
  // one lock/unlock cycle: true while an answer is picked (blocks re-picking
  // and blocks a stray double-fire on the Continue button), false again the
  // instant advanceToNext moves on.
  const actionLockRef = useRef(false);

  // `stat` table row id (see startStat/updateStat) + the running per-question
  // answer log persisted there — the source of truth for resuming a
  // rejoined/refreshed game from where the player left off.
  const [statId, setStatId] = useState(null);
  const [answerLog, setAnswerLog] = useState([]);

  useEffect(() => {
    gameCompletedRef.current = gameCompleted;
  }, [gameCompleted]);

  // Popup toggle (admin Rules page): when off, skip the explanation screen —
  // just flash a quick right/wrong animation and auto-advance to the next question.
  const popupEnabled = parseBool(themeData?.popup, false);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
      if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
    };
  }, []);

  const isDemoSession =
    sessionId === "demobypass" || storedUserData?.role === "demobypass" || storedUserData?.source === "DEMO";

  // 'GUEST_USER' | 'ORG_USER' | 'demobypass' — see src/pages/loginSlice.js.
  const userType = storedUserData?.role || storedUserData?.source || "";
  const employeeId =
    storedUserData?.employeeId || storedUserData?.emp_id || storedUserData?.empId || "";
  // Employee-code logins often have no first/last name on file — fall back
  // to the employeeId itself rather than leaving the stat row's name blank.
  const playerName = storedUserData?.name || employeeId || "";

  /** Push points + time to the external reports server (add or update).
   *  Returns the reportId now in effect (freshly created/updated, or the
   *  one already known) so callers can persist it immediately instead of
   *  reading the `reportId` state closure, which only reflects this update
   *  after the next render — see saveStat call sites below. */
  const pushExternalReport = useCallback(
    async (currentScore, currentTime) => {
      const role = storedUserData?.role || storedUserData?.source;
      if (role === "demobypass" || !userId) return reportId;

      try {
        const response = await sendReport({
          sessionId,
          organizationId,
          userId,
          role,
          token: storedUserData?.token,
          gameId: storedUserData?.gameId,
          name: storedUserData?.name || "",
          points: currentScore,
          time: formatTime(currentTime),
          reportId: reportId || undefined,
        });
        const newReportId = extractReportIdFromResponse(response);
        if (newReportId && newReportId !== reportId) {
          setReportId(newReportId);
          try {
            const merged = { ...storedUserData, reportId: newReportId };
            sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(merged));
          } catch {
            /* ignore */
          }
          return newReportId;
        }
        return reportId;
      } catch (error) {
        console.error("Failed to send external report:", error);
        return reportId;
      }
    },
    [organizationId, sessionId, userId, storedUserData, reportId]
  );

  /** Persists current_active (resume pointer), points, time, the answer log and
   *  the external reportId (once known) to `stat` — the only progress store now.
   *  `reportIdOverride` lets a caller that just resolved a fresh id from
   *  pushExternalReport pass it straight through instead of relying on the
   *  `reportId` state, which hasn't re-rendered into this closure yet. */
  const saveStat = useCallback(
    async (currentActive, currentPoints, currentTime, currentAnswerLog, status, reportIdOverride) => {
      if (!adminToken || !statId) return;
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateStat`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({
            id: statId,
            current_active: currentActive,
            points: currentPoints,
            time: formatTime(currentTime),
            answer: currentAnswerLog,
            status,
            reportId: reportIdOverride || reportId || undefined,
          }),
        });
      } catch (error) {
        console.error("Failed to save stat:", error);
      }
    },
    [adminToken, statId, reportId]
  );

  // ── Load questions, then create/resume the `stat` row for this play-through ──
  useEffect(() => {
    if (!adminToken || !themeData || !currentThemeName) return;

    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getMythFactQuestions`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ organizationId, sessionId, currentTheme: currentThemeName }),
        });
        const data = await res.json();
        if (!data.success || !Array.isArray(data.questions)) {
          throw new Error(data.message || "Failed to load questions");
        }

        if (data.questions.length < totalWanted) {
          // Not enough questions saved for this theme to fill the configured
          // count (covers zero questions too — 0 < totalWanted). Block play
          // entirely rather than starting with a shorter game: the popup
          // can't be dismissed by clicking away, and OK sends the player
          // back to the pre-game screen instead of into the quiz.
          const deskBg = themeData?.background_desk
            ? getDvSourceImageUrl(themeData.background_desk)
            : "";
          Swal.fire({
            title: "Not Enough Questions",
            text: "Please contact admin to add question.",
            icon: "warning",
            allowOutsideClick: false,
            allowEscapeKey: false,
            confirmButtonText: "OK",
            background: deskBg ? `${toCssUrlValue(deskBg)} center / cover no-repeat` : undefined,
          }).then(() => {
            navigate("/welcome", { replace: true });
          });
          setLoading(false);
          return;
        }

        // Shuffle the *entire* pool before slicing to `totalWanted` — shuffling
        // after the slice only reorders whichever questions happened to be
        // first in DB order, instead of picking a random subset of all of them.
        const pool = shuffleEnabled ? shuffleArray(data.questions) : data.questions;
        const candidateOrder = pool.slice(0, Math.min(totalWanted, pool.length));

        if (!userId) {
          // No user profile to key a stat row on — play without resume support.
          setQuestions(candidateOrder);
          setLoading(false);
          return;
        }

        try {
          const byId = new Map(data.questions.map((q) => [String(q.id), q]));
          const statRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/startStat`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
            body: JSON.stringify({
              organizationId,
              sessionId,
              userId,
              name: playerName,
              email: storedUserData?.email || "",
              userType,
              // Stable initial value only (not the live `reportId` state,
              // which changes after every answer and must not re-trigger this
              // load-once effect) — whatever reportId was already known when
              // this session started, e.g. resumed from sessionStorage.
              reportId: storedUserData?.reportId || storedUserData?.report_id || undefined,
              questionIds: candidateOrder.map((q) => q.id),
            }),
          });
          const statData = await statRes.json();

          if (!statData.success) {
            setQuestions(candidateOrder);
          } else {
            setStatId(statData.id);
            // React bails out of a re-render if this is already the current
            // value, so no `!reportId` guard is needed (and none should be
            // read here — see the reportId comment above on why it's kept
            // out of this effect's dependency array).
            if (statData.reportid) setReportId(statData.reportid);

            const orderedQuestions = Array.isArray(statData.question)
              ? statData.question.map((id) => byId.get(String(id))).filter(Boolean)
              : [];
            const finalQuestions = orderedQuestions.length ? orderedQuestions : candidateOrder;
            setQuestions(finalQuestions);

            const resumedAnswers = Array.isArray(statData.answer) ? statData.answer : [];
            setAnswerLog(resumedAnswers);
            setResults(resumedAnswers.map((a) => !!a?.isCorrect));
            setScore(Number(statData.points) || 0);
            setElapsed(parseTimeToSeconds(statData.time));

            const resumeIdx = Number(statData.current_active) || 0;
            if (resumeIdx >= finalQuestions.length) {
              // Every question was already answered on a previous visit —
              // go straight to the leaderboard, no intermediate screen.
              gameCompletedRef.current = true;
              setGameCompleted(true);
              navigate("/leaderboard", { replace: true });
            } else {
              setIdx(resumeIdx);
            }
          }
        } catch (statError) {
          console.error("Failed to start/resume stat:", statError);
          setQuestions(candidateOrder);
        }

        setLoading(false);
      } catch (e) {
        Swal.fire({ title: "Error", text: e.message || "Failed to load questions", icon: "error" });
        setLoading(false);
      }
    })();
    // `reportId` is deliberately excluded: it's live state updated after every
    // answer, and this effect must only run once per load (see the comment
    // above the startStat call). `navigate` (from useNavigate) is referentially
    // stable across renders, safe to omit too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adminToken,
    themeData,
    currentThemeName,
    organizationId,
    sessionId,
    totalWanted,
    shuffleEnabled,
    userId,
    storedUserData,
    playerName,
    userType,
  ]);

  // ── Timer ──
  useEffect(() => {
    if (loading || gameCompleted) return undefined;
    const timer = setInterval(() => setElapsed((prev) => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [loading, gameCompleted]);

  // ── Periodic stat save — mainly to keep `time` current while a question sits
  //    open a while; every answer already saves immediately via pickAnswer ──
  useEffect(() => {
    if (loading || gameCompleted) return undefined;
    const saveInterval = setInterval(() => saveStat(idx, score, elapsed, answerLog, "inprogress"), 30000);
    return () => clearInterval(saveInterval);
  }, [loading, gameCompleted, idx, score, elapsed, answerLog, saveStat]);

  const currentQuestion = questions[idx] || null;

  const pickAnswer = (optionNumber) => {
    if (actionLockRef.current || locked || !currentQuestion) return;
    actionLockRef.current = true;
    setLocked(true);

    const correctId = String(currentQuestion.answer_id) === "2" ? 2 : 1;
    const isCorrect = optionNumber === correctId;
    const newResults = [...results, isCorrect];
    setResults(newResults);

    const newScore = isCorrect ? score + pointsPerCorrect : score;
    if (isCorrect) setScore(newScore);

    setLastAnswer({
      chosen: optionNumber,
      correctId,
      isCorrect,
      correctText: correctId === 1 ? currentQuestion.option_one : currentQuestion.option_two,
    });

    // current_active = index of the next unanswered question, so a
    // rejoin/refresh resumes right after this one instead of from the start.
    const newAnswerLog = [
      ...answerLog,
      {
        questionId: currentQuestion.id,
        chosen: optionNumber,
        chosenText: optionNumber === 1 ? currentQuestion.option_one : currentQuestion.option_two,
        correctId,
        isCorrect,
        time: formatTime(elapsed),
      },
    ];
    setAnswerLog(newAnswerLog);
    // Resolve the reportId from the report push first, then save — passing
    // it through explicitly avoids a race where saveStat fires with the
    // stale `reportId` state (not yet re-rendered) and the freshly
    // created/updated external reportId never reaches `stat.reportid`.
    //
    // Skipped on the last question: finishGame below fires its own
    // pushExternalReport/saveStat("completed") for the same row a moment
    // later. Firing both let this "inprogress" write land *after* finishGame's
    // "completed" write on a slow/contended request, flipping the row back
    // to inprogress right after it was marked done — which is exactly what
    // Dr Reddy's external leaderboard reads, so it'd show the game as still
    // in progress. finishGame is the sole writer for the final answer.
    const isLastQuestion = idx >= questions.length - 1;
    if (!isLastQuestion) {
      (async () => {
        const freshReportId = await pushExternalReport(newScore, elapsed);
        saveStat(idx + 1, newScore, elapsed, newAnswerLog, "inprogress", freshReportId);
      })();
    }

    if (isCorrect && confettiHostRef.current) {
      launchConfetti(confettiHostRef.current, 0.5);
    }

    if (popupEnabled) {
      setScreen("verdict");
      return;
    }

    // Popup off: skip the explanation screen entirely — just flash a quick
    // right/wrong animation on the question itself, then move straight on.
    if (!isCorrect) {
      setShakeActive(true);
      shakeTimeoutRef.current = setTimeout(() => setShakeActive(false), 550);
    }
    autoAdvanceTimeoutRef.current = setTimeout(() => {
      advanceToNext(newScore, newResults, newAnswerLog);
    }, 1100);
  };

  const finishGame = useCallback(
    async (finalScore, finalResults, finalAnswerLog) => {
      if (gameCompletedRef.current) return;
      gameCompletedRef.current = true;
      setGameCompleted(true);

      if (confettiHostRef.current) {
        const correctCount = finalResults.filter(Boolean).length;
        launchConfetti(confettiHostRef.current, correctCount / (finalResults.length || 1));
      }

      const freshReportId = await pushExternalReport(finalScore, elapsed);
      await saveStat(questions.length, finalScore, elapsed, finalAnswerLog, "completed", freshReportId);

      try {
        const merged = { ...storedUserData, gameover: 1 };
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(merged));
      } catch {
        /* ignore */
      }

      // No intermediate "Game complete" screen — go straight to the
      // leaderboard. The short pause just lets the confetti burst above be
      // seen rather than getting cut off by an instant unmount.
      await new Promise((resolve) => setTimeout(resolve, 900));
      navigate("/leaderboard", { replace: true });
    },
    [pushExternalReport, saveStat, elapsed, questions.length, storedUserData, navigate]
  );

  // Advances to the next question (or finishes the game). Called directly by
  // the "Continue" button (popup on) and by the auto-advance timer in
  // pickAnswer (popup off) — the latter passes the just-computed score/results
  // explicitly since React state hasn't re-rendered yet inside that timeout.
  const advanceToNext = (finalScore = score, finalResults = results, finalAnswerLog = answerLog) => {
    // Not currently in an "answered, waiting to move on" state — either
    // nothing's been picked yet, or a rapid second call (double-clicked
    // Continue, or the auto-advance timer racing a manual click) already
    // consumed the pending advance. Either way, there's nothing to do.
    if (!actionLockRef.current) return;
    actionLockRef.current = false;

    // Demo sessions get a single question, then are upsold to subscribe.
    if (isDemoSession && idx === 0) {
      gameCompletedRef.current = true;
      setGameCompleted(true);
      Swal.fire({
        title: "Thank you for playing!",
        text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
        icon: "info",
        confirmButtonText: "View Plans",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => {
        window.location.href = `${process.env.REACT_APP_BASE_URL || ""}/plans`;
      });
      return;
    }

    setLocked(false);
    setLastAnswer(null);

    if (idx >= questions.length - 1) {
      finishGame(finalScore, finalResults, finalAnswerLog);
      return;
    }
    setIdx((prev) => prev + 1);
    setScreen("question");
  };

  if (loading) {
    return (
      <div className="mf-loading">
        <p>Loading questions...</p>
      </div>
    );
  }

  return (
    <div className="mf-root" style={themeVars}>
      <div className="mf-confetti-host" aria-hidden="true" ref={confettiHostRef} />

      <RulesModal open={rulesModalOpen} onClose={() => setRulesModalOpen(false)} />

      <div className="mf-app">
        <header className="mf-topbar">
          <div className="mf-hud">
            <div className="mf-progress" aria-label="Question progress">
              {questions.map((_, i) => (
                <span
                  key={i}
                  className={[
                    "mf-seg",
                    i < idx ? (results[i] ? "mf-done-fact" : "mf-done-myth") : "",
                    i === idx ? "mf-current" : "",
                  ].filter(Boolean).join(" ")}
                />
              ))}
            </div>
            <div className={`mf-chip${lastAnswer?.isCorrect ? " mf-pulse" : ""}`}>
              <span className="mf-coin" aria-hidden="true"></span>
              <span>Points&nbsp;<strong>{score}</strong></span>
            </div>
            <button
              type="button"
              className="mf-help-btn"
              onClick={() => setRulesModalOpen(true)}
              aria-label="How to play"
            >
              ?
            </button>
          </div>
        </header>

        {/* SCREEN: QUESTION */}
        <section className={`mf-screen${screen === "question" ? " mf-active" : ""}`}>
          <div className="mf-stage">
            {!currentQuestion && questions.length === 0 && (
              <p className="mf-q-eyebrow">No questions available right now.</p>
            )}
            {currentQuestion && (
              <div className={`mf-q-wrap${shakeActive ? " mf-shake" : ""}`}>
                <p className="mf-q-eyebrow">Question {idx + 1} of {questions.length}</p>
                <div className="mf-q-card">
                  <span className="mf-ribbon" aria-hidden="true"></span>
                  <p className="mf-q-text">{currentQuestion.question_name}</p>
                </div>
                <div className="mf-choices">
                  <button className="mf-choice mf-choice-a" onClick={() => pickAnswer(1)} disabled={locked}>
                    <span className="mf-choice-label">{currentQuestion.option_one}</span>
                    {optionOneDescription ? (
                      <span className="mf-choice-desc">{optionOneDescription}</span>
                    ) : null}
                  </button>
                  <button className="mf-choice mf-choice-b" onClick={() => pickAnswer(2)} disabled={locked}>
                    <span className="mf-choice-label">{currentQuestion.option_two}</span>
                    {optionTwoDescription ? (
                      <span className="mf-choice-desc">{optionTwoDescription}</span>
                    ) : null}
                  </button>
                </div>
                <p className="mf-q-key-note">Choose the option you believe is correct</p>
              </div>
            )}
          </div>
        </section>

        {/* SCREEN: VERDICT */}
        <section className={`mf-screen${screen === "verdict" ? " mf-active" : ""}`}>
          <div className="mf-stage">
            {lastAnswer && currentQuestion && (
              <div className="mf-verdict">
                <div className="mf-v-banner">
                  <span className={`mf-stamp ${lastAnswer.correctId === 1 ? "mf-is-a" : "mf-is-b"}`}>
                    {lastAnswer.correctText}
                  </span>
                  <span className={`mf-v-result ${lastAnswer.isCorrect ? "mf-good" : "mf-bad"}`}>
                    {lastAnswer.isCorrect ? (
                      <>Correct answer&nbsp;<span className="mf-pts">+{pointsPerCorrect} pts</span></>
                    ) : (
                      <>Incorrect answer&nbsp;<span className="mf-pts">+0 pts</span></>
                    )}
                  </span>
                </div>
                <div className="mf-v-card">
                  <span className="mf-ribbon" aria-hidden="true"></span>
                  <p className="mf-v-statement">{currentQuestion.question_name}</p>
                  <p className="mf-v-explain-label">Explanation</p>
                  <p className="mf-v-explain">{currentQuestion.information}</p>
                </div>
                <div className="mf-v-cta">
                  <button className="mf-btn mf-btn-primary" onClick={() => advanceToNext()}>Continue</button>
                </div>
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
  );
}
